import { useRef } from 'react';
import { gsap, useGSAP, EASE } from './gsap';

type Options = {
  /** Selector for the children to stagger in, scoped to the container. */
  selector: string;
  /** Seconds between each child. */
  stagger?: number;
  /** Distance in px the children travel upward. */
  y?: number;
  /** Viewport position at which the reveal fires. */
  start?: string;
};

/**
 * Reveals a set of children once, as the container scrolls into view.
 *
 * Returns a ref to attach to the container. Tweens, ScrollTriggers and the
 * matchMedia instance are all torn down on unmount, which matters under
 * StrictMode: without the explicit `mm.revert()` the first mount's trigger
 * outlives its tween and the reveal gets stuck half-applied.
 */
export function useScrollReveal<T extends HTMLElement>({
  selector,
  stagger = 0.07,
  y = 16,
  start = 'top 80%',
}: Options) {
  const container = useRef<T>(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        // fromTo, not from: explicit endpoints survive a remount mid-tween.
        gsap.fromTo(
          selector,
          { opacity: 0, y },
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
            ease: EASE,
            stagger,
            // ScrollTrigger defers immediateRender, which leaves every target
            // but the first visible until the trigger fires — they would snap
            // to hidden and fade back in. Forcing it applies the from-state to
            // all of them up front.
            immediateRender: true,
            scrollTrigger: { trigger: container.current, start, once: true },
          },
        );
      });

      return () => mm.revert();
    },
    { scope: container, dependencies: [selector, stagger, y, start] },
  );

  return container;
}
